import { TASK_STATUSES, type TaskLink, type TaskStatus } from "@esi/shared";
import { Circle, CircleCheck, CirclePause, Clock, TriangleAlert } from "lucide-react";
import { type ReactNode, useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { projectConversationPath, projectHomePath } from "../router.tsx";
import type { StatusFilter } from "./queries.ts";

export const textAreaClass =
  "min-h-28 w-full resize-y rounded-md border border-input bg-surface px-3 py-2 text-sm text-fg outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";
export function TaskSelect<T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string }[];
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <label className="text-sm font-medium" htmlFor={id}>
        {label}
      </label>
      <NativeSelect
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        disabled={disabled}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}
export function StatusFilterField({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const { t } = useTranslation();
  return (
    <TaskSelect
      label={t("tasks.status")}
      value={value}
      onChange={onChange}
      options={(["open", "all", "todo", "in_progress", "blocked", "done"] as const).map(
        (value) => ({ value, label: t(`tasks.${value}`) }),
      )}
    />
  );
}
const STATUS_ICONS = { todo: Circle, in_progress: Clock, blocked: CirclePause, done: CircleCheck };
function StatusFace({ status }: { status: TaskStatus }) {
  const { t } = useTranslation();
  const Icon = STATUS_ICONS[status];
  return (
    <>
      <Icon aria-hidden="true" className="size-4 shrink-0 text-fg-secondary" strokeWidth={1.75} />
      <span className="truncate">{t(`tasks.${status}`)}</span>
    </>
  );
}
export function TaskStatusText({ status }: { status: TaskStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-fg-secondary">
      <StatusFace status={status} />
    </span>
  );
}
/**
 * The status column of a task row: one width for picker and label, and the same text inset, so
 * a list of mixed rows stays aligned (the RolePicker/RoleLabel pattern). 44px on phones.
 */
const STATUS_CELL = "h-8 w-40 px-3 max-sm:h-11";
/** A status shown in a row, not offered: same width and inset as the picker. */
export function RowStatusLabel({ status }: { status: TaskStatus }) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 border border-transparent text-sm text-fg-secondary",
        STATUS_CELL,
      )}
    >
      <StatusFace status={status} />
    </span>
  );
}
/**
 * A row's status picker (tasks-quick-status): quiet like the label until hovered, focused or
 * open (always outlined where there is no hover), and saved on choice. Named by its task.
 */
export function RowStatusPicker({
  status,
  label,
  busy,
  onChange,
}: {
  status: TaskStatus;
  label: string;
  busy: boolean;
  onChange: (status: TaskStatus) => void;
}) {
  return (
    <Select
      value={status}
      onValueChange={(next) => {
        const chosen = TASK_STATUSES.find((s) => s === next);
        // One write at a time per row: a choice while saving is ignored, never queued.
        if (chosen && chosen !== status && !busy) {
          onChange(chosen);
        }
      }}
    >
      <SelectTrigger
        size="sm"
        aria-label={label}
        aria-busy={busy || undefined}
        className={cn(
          STATUS_CELL,
          "justify-start gap-1.5 border-transparent text-fg-secondary shadow-none hover:border-input hover:text-fg data-[state=open]:border-input aria-busy:opacity-70 dark:bg-transparent [@media(hover:none)]:border-input [&>svg:last-child]:ml-auto",
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <StatusFace status={status} />
        </span>
      </SelectTrigger>
      <SelectContent position="popper" align="start" className="min-w-40">
        {TASK_STATUSES.map((value) => (
          <SelectItem key={value} value={value}>
            <StatusFace status={value} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
export function calendarDate(day: string, locale: string) {
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeZone: "UTC" }).format(
    new Date(`${day}T00:00:00Z`),
  );
}
export function isOverdue(
  due: string | null,
  status: TaskStatus,
  today = new Date().toISOString().slice(0, 10),
) {
  return due !== null && status !== "done" && due < today;
}
export function DueDate({ due, status }: { due: string | null; status: TaskStatus }) {
  const { t, i18n } = useTranslation();
  const overdue = isOverdue(due, status);
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-sm text-fg-secondary tabular-nums">
      {due ? <time dateTime={due}>{calendarDate(due, i18n.language)}</time> : t("tasks.noDue")}
      {overdue ? (
        <span className="inline-flex items-center gap-1 text-warning-fg">
          <TriangleAlert className="size-3.5" aria-hidden="true" />
          {t("tasks.overdue")}
        </span>
      ) : null}
    </span>
  );
}
export function TaskError({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useTranslation();
  return (
    <Notice
      tone="danger"
      action={
        retry ? (
          <Button variant="outline" size="sm" onClick={retry}>
            {t("app.retry")}
          </Button>
        ) : undefined
      }
    >
      {error instanceof ApiError ? error.message : t("errors.network")}
    </Notice>
  );
}
export function writeError(error: unknown, t: (key: "tasks.uncertain") => string) {
  return error instanceof ApiError && error.status < 500 ? error.message : t("tasks.uncertain");
}
export function TaskHeading({ children, focus = true }: { children: ReactNode; focus?: boolean }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus) {
      ref.current?.focus();
    }
  }, [focus]);
  return (
    <h1
      ref={ref}
      tabIndex={-1}
      className="min-w-0 text-xl font-semibold tracking-tight break-words text-fg outline-none"
    >
      {children}
    </h1>
  );
}
export function sourcePath(projectId: string, link: TaskLink): string | null {
  if (!link.available) {
    return null;
  }
  switch (link.kind) {
    case "document":
      return `${projectHomePath(projectId, "files")}?document=${encodeURIComponent(link.targetId)}`;
    case "conversation":
      return projectConversationPath(projectId, link.targetId);
    case "message":
      return link.conversationId ? projectConversationPath(projectId, link.conversationId) : null;
    case "artefact":
      return link.conversationId
        ? `${projectConversationPath(projectId, link.conversationId)}?artefact=${encodeURIComponent(link.targetId)}`
        : null;
  }
}
